import { Helmet } from 'react-helmet-async';
import { useEffect, useState, useCallback } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, MenuItem, Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import Iconify from '../../components/iconify';
import { useProject } from '../../hooks/ProjectContext';
import SitePicker from './SitePicker';

function bearerHeaders() {
  const tkn = localStorage.getItem('tkn');
  return { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) };
}

export default function MeasureSettingsPage() {
  const { selectedProject, selectedSite, setSelectedSite } = useProject();
  const [tab, setTab] = useState('devices');

  if (!selectedProject) return <EmptyState message="Pick a project from the header." />;
  if (!selectedSite) return (
    <SitePicker
      project={selectedProject}
      pageTitle="Settings"
      pageDescription="Plumbing for the Measure surface — devices, model defaults, integrations."
      onPickSite={setSelectedSite}
    />
  );

  return (
    <>
      <Helmet><title>Settings | Measure</title></Helmet>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Box>
            <Typography variant="overline" color="text.secondary">{selectedProject.name} / {selectedSite.name}</Typography>
            <Typography variant="h4">Settings</Typography>
          </Box>

          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab value="devices" label="Devices" />
            <Tab value="model" label="Model defaults" />
            <Tab value="integrations" label="Integrations" />
          </Tabs>

          {tab === 'devices' && <DevicesTab site={selectedSite} />}
          {tab === 'model' && (
            <Alert severity="info">Model defaults (IPCC tier, species → rate overrides, slope adjustments). Phase 2.</Alert>
          )}
          {tab === 'integrations' && (
            <Alert severity="info">External data feeds: Tel-Hai EC tower (s3://eddyco/), MQTT brokers, Haifa API. Phase 1–2.</Alert>
          )}
        </Stack>
      </Container>
    </>
  );
}

function DevicesTab({ site }) {
  const [sensors, setSensors] = useState([]);
  const [strata, setStrata] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bindOpen, setBindOpen] = useState(false);

  const reload = useCallback(() => {
    setLoading(true); setError('');
    Promise.all([
      fetch(`/v1/flux/sites/${site.id}/sensors`, { headers: bearerHeaders() }).then(r => r.json()),
      fetch(`/v1/flux/sites/${site.id}/strata`, { headers: bearerHeaders() }).then(r => r.json()),
    ])
      .then(([sn, st]) => {
        if (!sn.ok) throw new Error(sn.message);
        setSensors(sn.sensors || []);
        setStrata(st.strata || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [site.id]);

  useEffect(() => { reload(); }, [reload]);

  const unbind = useCallback(async (sensorId) => {
    setError('');
    try {
      const r = await fetch(`/v1/flux/sensors/${sensorId}`, { method: 'DELETE', headers: bearerHeaders() });
      const j = await r.json();
      if (!j.ok) throw new Error(j.message);
      reload();
    } catch (e) { setError(e.message); }
  }, [reload]);

  const sequest = sensors.filter(s => s.type === 'sequestometer');
  const ec = sensors.filter(s => s.type === 'ec_tower');

  return (
    <Stack spacing={2.5}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      <Typography variant="body2" color="text.secondary">
        Sensors bound to this site supply the live data feed. Each sensor produces NEE/GPP/Reco when paired with a
        K calibration.
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Bound sensors ({sequest.length} SequestOmeter, {ec.length} EC)
        </Typography>
        <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => setBindOpen(true)}>
          Bind sensor
        </Button>
      </Box>

      {loading ? <CircularProgress size={24} /> : (
        <Stack spacing={1.5}>
          {sensors.map(s => <SensorRow key={s.id} sensor={s} strata={strata} onUnbind={() => unbind(s.id)} />)}
          {sensors.length === 0 && <Alert severity="info">No sensors bound yet.</Alert>}
        </Stack>
      )}

      <BindSensorDialog
        open={bindOpen}
        onClose={() => setBindOpen(false)}
        siteId={site.id}
        strata={strata}
        onBound={() => { setBindOpen(false); reload(); }}
      />
    </Stack>
  );
}

function SensorRow({ sensor, strata, onUnbind }) {
  const stratum = strata.find(s => s.id === sensor.stratum_id);
  const lastReading = sensor.last_reading;
  return (
    <Box sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
      <Iconify
        icon={sensor.type === 'ec_tower' ? 'eva:radio-fill' : 'eva:wifi-fill'}
        sx={{ fontSize: 28, color: lastReading ? 'success.main' : 'text.disabled' }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>{sensor.label || sensor.wfmc}</Typography>
          <Chip label={sensor.type === 'ec_tower' ? 'EC' : 'SequestOmeter'} size="small" />
          {stratum && <Chip label={stratum.name} size="small" variant="outlined" />}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {sensor.wfmc}
          {lastReading
            ? ` · last reading ${new Date(lastReading.time).toLocaleString()}`
            : ' · no recent data'}
        </Typography>
      </Box>
      <IconButton size="small" onClick={onUnbind} title="Unbind sensor">
        <Iconify icon="eva:close-fill" sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}

function BindSensorDialog({ open, onClose, siteId, strata, onBound }) {
  const [wfmc, setWfmc] = useState('');
  const [label, setLabel] = useState('');
  const [type, setType] = useState('sequestometer');
  const [stratumId, setStratumId] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async () => {
    setSaving(true); setErr('');
    try {
      const r = await fetch(`/v1/flux/sites/${siteId}/sensors`, {
        method: 'POST', headers: bearerHeaders(),
        body: JSON.stringify({ wfmc, label, type, stratum_id: stratumId || null }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.message);
      setWfmc(''); setLabel(''); setStratumId('');
      onBound();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bind a sensor</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {err && <Alert severity="error">{err}</Alert>}
          <TextField label="WFMC (device identifier)" value={wfmc} onChange={e => setWfmc(e.target.value)} fullWidth size="small" autoFocus />
          <TextField label="Label (optional)" value={label} onChange={e => setLabel(e.target.value)} fullWidth size="small" />
          <TextField select label="Type" value={type} onChange={e => setType(e.target.value)} fullWidth size="small">
            <MenuItem value="sequestometer">SequestOmeter</MenuItem>
            <MenuItem value="ec_tower">EC tower</MenuItem>
          </TextField>
          <TextField select label="Stratum (optional)" value={stratumId} onChange={e => setStratumId(e.target.value)} fullWidth size="small">
            <MenuItem value="">— None (site-wide)</MenuItem>
            {strata.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!wfmc || saving}>
          {saving ? 'Binding…' : 'Bind'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EmptyState({ message }) {
  return (
    <Container maxWidth="md">
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 1 }}>Settings</Typography>
        <Typography color="text.secondary">{message}</Typography>
      </Box>
    </Container>
  );
}
